import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { FilterX, InfoIcon } from "lucide-react";
import { useChoices, useInventory, useInventoryExport, useMeterModels } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { logEvent } from "@shared/analytics/analytics";

interface InventoryFilters {
  type: string;
  manufacturer: string;
}

const MeterInventoryList = () => {
  const utilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();

  // Internal pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Read filters from URL (excluding page)
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const type = searchParams.get("type") || "";
  const manufacturer = searchParams.get("manufacturer") || "";

  // pendingFilters: UI state for user edits
  const [pendingFilters, setPendingFilters] = useState<InventoryFilters>({
    type: decodeURIComponent(type),
    manufacturer: decodeURIComponent(manufacturer),
  });

  // sync pendingFilters when URL params change
  useEffect(() => {
    setPendingFilters({
      type: decodeURIComponent(type),
      manufacturer: decodeURIComponent(manufacturer),
    });
  }, [type, manufacturer]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, type, manufacturer]);

  const handlePendingFilterChange = (
    key: keyof InventoryFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const queryParams = useMemo(
    () => ({
      remoteUtilityId: utilityId,
      tab: "in_stock",
      page: currentPage,
      limit: PAGE_SIZE,
      search: searchQuery,
      meter_type: type,
      manufacturer: manufacturer,
    }),
    [utilityId, currentPage, searchQuery, type, manufacturer]
  );

  const { data: inStockList, isLoading } = useInventory(queryParams);
  const { data: meterType } = useChoices({ choice: "meter_type" });
  const { data: meterModels } = useMeterModels({ remoteUtilityId: utilityId });
  const { exportCSV, isExporting } = useInventoryExport(
    utilityId,
    "in_stock",
    searchQuery,
    type,
    manufacturer
  );

  // Extract unique manufacturer names from meter models
  const uniqueManufacturers = useMemo(() => {
    if (!meterModels) return [];
    const manufacturersSet = new Set<string>();
    meterModels.forEach((model) => {
      if (model.meterMake) manufacturersSet.add(model.meterMake);
    });
    return Array.from(manufacturersSet).sort();
  }, [meterModels]);

  const processedData = useMemo(() => {
    return (
      inStockList?.results.map((item) => ({
        ...item,
        make: item?.meterModel?.make,
        model: item?.meterModel?.model,
        id: item?.deviceNo,
      })) ?? []
    );
  }, [inStockList]);

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.type && { type: pendingFilters.type }),
      ...(pendingFilters.manufacturer && {
        manufacturer: pendingFilters.manufacturer,
      }),
    };

    // Remove page from URL params since we handle it internally
    delete newParams.page;

    setSearchParams(newParams);
    logEvent('MX Inventory Filters Applied', {
      ...newParams,
      searchQuery,
    })
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.type;
    delete newParams.manufacturer;
    delete newParams.search;
    delete newParams.page; // Remove page from URL

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(type && { type }),
      ...(manufacturer && { manufacturer }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search; // remove from URL if search is empty
    }

    // Remove page from URL params
    delete newParams.page;

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderSpecifications = (specs: any) => {
    if (!specs) return "No specifications available";

    const formatValue = (value: any, unit?: string) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === 0
      ) {
        return "N/A";
      }
      return unit ? `${value} ${unit}` : value;
    };

    return (
      <div className="space-y-2 text-sm max-w-xs">
        <div className="font-semibold text-gray-900 mb-2">
          Technical Specifications
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div className="text-gray-600">Width:</div>
          <div className="text-gray-900">{formatValue(specs.width, "mm")}</div>

          <div className="text-gray-600">Height:</div>
          <div className="text-gray-900">{formatValue(specs.height, "mm")}</div>

          <div className="text-gray-600">Length:</div>
          <div className="text-gray-900">{formatValue(specs.length, "mm")}</div>

          <div className="text-gray-600">Weight:</div>
          <div className="text-gray-900">{formatValue(specs.weight, "kg")}</div>

          <div className="text-gray-600">Accuracy:</div>
          <div className="text-gray-900">{formatValue(specs.accuracy)}</div>

          <div className="text-gray-600">Material:</div>
          <div className="text-gray-900">{formatValue(specs.material)}</div>

          <div className="text-gray-600">Max Flow:</div>
          <div className="text-gray-900">
            {formatValue(specs.maxFlow, "L/min")}
          </div>

          <div className="text-gray-600">IP Rating:</div>
          <div className="text-gray-900">{formatValue(specs.ipRating)}</div>

          <div className="text-gray-600">Dial Count:</div>
          <div className="text-gray-900">{formatValue(specs.dialCount)}</div>

          <div className="text-gray-600">Dial Length:</div>
          <div className="text-gray-900">{formatValue(specs.dialLength)}</div>

          <div className="text-gray-600">Connection Size:</div>
          <div className="text-gray-900">
            {formatValue(specs.connectionSize)}
          </div>
        </div>

        {specs.meterConfiguration && (
          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="font-medium text-gray-900 mb-1">Configuration</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <div className="text-gray-600">Read Type:</div>
              <div className="text-gray-900">
                {formatValue(specs.meterConfiguration.readType)}
              </div>

              <div className="text-gray-600">Display Type:</div>
              <div className="text-gray-900">
                {formatValue(specs.meterConfiguration.displayType)}
              </div>

              <div className="text-gray-600">Tamper Proof:</div>
              <div className="text-gray-900">
                {specs.meterConfiguration.tamperProof ? "Yes" : "No"}
              </div>

              <div className="text-gray-600">Read Frequency:</div>
              <div className="text-gray-900">
                {formatValue(specs.meterConfiguration.readFrequency)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns: TableColumn<any>[] = [
    {
      key: "deviceNo",
      header: "Serial/Device Number",
      sortable: true,
    },
    {
      key: "model",
      header: "Model",
      sortable: true,
    },
    {
      key: "meterTypeDisplay",
      header: "Type",
      type: "badge",
      badgeConfig: {
        getClassName: (value) => {
          switch (value?.toLowerCase()) {
            case "photo meter":
              return "bg-blue-50 text-blue-500";
            case "smart meter":
              return "bg-green-50 text-green-500";
            case "manual meter":
              return "bg-orange-50 text-orange-500";
            case "headend meter":
              return "bg-purple-50 text-purple-500";
            case "prepaid meter":
              return "bg-yellow-50 text-yellow-500";
            default:
              return "bg-blue-500";
          }
        },
      },
    },
    {
      key: "meterMake",
      header: "Manufacturer",
      sortable: true,
    },
    {
      key: "premise",
      header: "Location",
    },
    {
      key: "specifications",
      header: "Specification",
      render: (_, row) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <InfoIcon size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" className="w-62">
              {renderSpecifications(row.meterModel?.technicalSpecifications)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
  ];

  const pagination = inStockList
    ? {
        currentPage,
        totalPages: Math.ceil(inStockList.count / PAGE_SIZE),
        totalItems: inStockList.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, inStockList.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Device Type</label>
          <Select
            value={pendingFilters.type}
            onValueChange={(value) => handlePendingFilterChange("type", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Device Type" />
            </SelectTrigger>
            <SelectContent>
              {meterType && meterType.length > 0 ? (
                meterType.map((type) => (
                  <SelectItem key={type.key} value={String(type.key)}>
                    {type.value}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value="no-data">
                  No data available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Manufacturer</label>
          <Select
            value={pendingFilters.manufacturer}
            onValueChange={(value) =>
              handlePendingFilterChange("manufacturer", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Manufacturer" />
            </SelectTrigger>
            <SelectContent>
              {uniqueManufacturers.length > 0 ? (
                uniqueManufacturers.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value="no-data">
                  No data available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={resetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters = type || manufacturer;
  const handleExport = () => {
    exportCSV();
  };

  return (
    <div className="space-y-4">
      <GenericTable
        data={processedData}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        actionsType="icons"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by serial number, model, or device number..."
        filterPanel={filterPanel}
        onExport={handleExport}
        exportLoading={isExporting}
        emptyMessage="No meters found matching your criteria"
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {!!type || !!manufacturer} filter(s) active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default MeterInventoryList;